import {
  Alert,
  Box,
  Button,
  Grid,
  Header,
  Modal,
  SpaceBetween,
  StatusIndicator,
} from '@cloudscape-design/components';
import { travelerFromFirestore } from '@parallel-fluidics/shared/models/travelers';
import reconcileInventoryTravelers from 'features/inventory/apis/reconcileInventoryTravelers';
import useExpectedInventoryTravelers from 'features/inventory/hooks/useExpectedInventoryTravelers';
import useRealtimeInventoryByProductVersion from 'features/inventory/hooks/useRealtimeInventoryByProductVersion';
import useGetProductById from 'features/products/hooks/useGetProductById';
import useGetProductVersion from 'features/products/hooks/useGetProductVersion';
import { doc, getDoc, getFirestore } from 'firebase/firestore';
import moment from 'moment';
import { useUserData } from 'providers/userDataProvider';
import React, {
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import classifyScan, { ScanStatus } from './classifyScan';
import Discrepancies from './Discrepancies';
import ScanFeed from './ScanFeed';
import SessionProgress from './SessionProgress';

// ~1.25fr / 1fr split from the design handoff, stacked on small screens.
const GRID_DEFINITION = [
  { colspan: { default: 12, xs: 7 } },
  { colspan: { default: 12, xs: 5 } },
];

export default function ReconciliationPage() {
  const { productDocId, versionId } = useParams();
  const navigate = useNavigate();
  const { user } = useUserData();
  const operatorEmail = user?.email ?? 'unknown';

  const seenIdsRef = useRef(new Set());
  const seqRef = useRef(0);
  const startedAtRef = useRef(moment());

  const [scans, setScans] = useState([]);
  const [filterValue, setFilterValue] = useState('all');
  const [inlineError, setInlineError] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const {
    data: product,
    isLoading: productLoading,
    error: productError,
  } = useGetProductById(productDocId);
  const {
    data: version,
    isLoading: versionLoading,
  } = useGetProductVersion(productDocId, versionId);
  const {
    data: expectedTravelers = [],
    isLoading: expectedLoading,
    error: expectedError,
  } = useExpectedInventoryTravelers(productDocId, versionId);
  const {
    data: inventory,
    status: inventoryStatus,
  } = useRealtimeInventoryByProductVersion(productDocId, versionId);

  const expectedSet = useMemo(
    () => new Set(expectedTravelers.map((t) => t.id)),
    [expectedTravelers],
  );

  const addScanRow = useCallback((id, status, otherSkuLabel) => {
    seqRef.current += 1;
    setScans((prev) => [
      {
        key: `${id}-${seqRef.current}`, id, status, otherSkuLabel, at: moment(),
      },
      ...prev,
    ]);
  }, []);

  // Shared scan pipeline — ScanField feeds it parsed traveler IDs
  // from both the handheld-scanner input and the camera.
  const processScan = useCallback(async (travelerId) => {
    setInlineError('');

    if (seenIdsRef.current.has(travelerId)) {
      setInlineError(`${travelerId}: duplicate scan — ignored.`);
      return;
    }
    // Reserve the ID before the async load so a rapid double-scan of the same
    // traveler can't pass the duplicate check twice; released below unless the
    // scan produced a feed row, so a failed scan can be retried.
    seenIdsRef.current.add(travelerId);
    let rowAdded = false;

    try {
      const snap = await getDoc(doc(getFirestore(), 'travelers', travelerId));
      if (!snap.exists()) {
        setInlineError(`${travelerId}: traveler not found.`);
        return;
      }
      const traveler = travelerFromFirestore(snap);
      const result = classifyScan({
        traveler,
        session: {
          productDocId,
          versionId,
          projectId: version?.projectId,
          pPartId: version?.pPartId,
          expectedSet,
        },
      });

      if (result.error) {
        setInlineError(result.error);
        return;
      }

      addScanRow(travelerId, result.status, result.otherSkuLabel);
      rowAdded = true;
    } catch (err) {
      setInlineError(err.message || 'Failed to load traveler.');
    } finally {
      if (!rowAdded) seenIdsRef.current.delete(travelerId);
    }
  }, [addScanRow, productDocId, versionId, version, expectedSet]);

  const derived = useMemo(() => {
    const countedSet = new Set(
      scans.filter((s) => s.status === ScanStatus.COUNTED).map((s) => s.id),
    );
    const notExpected = scans.filter((s) => s.status === ScanStatus.NOT_EXPECTED);
    const otherSku = scans.filter((s) => s.status === ScanStatus.OTHER_SKU);
    const counted = countedSet.size;
    const expected = expectedSet.size;
    const missingIds = [...expectedSet].filter((id) => !countedSet.has(id));
    const unexpected = notExpected.length;
    const missing = missingIds.length;
    const net = unexpected - missing;
    const percent = expected > 0 ? Math.round((counted / expected) * 100) : 0;
    const commitIds = [...new Set([...countedSet, ...notExpected.map((s) => s.id)])];
    return {
      countedSet,
      counted,
      expected,
      missingIds,
      missing,
      notExpected,
      unexpected,
      otherSku,
      net,
      percent,
      commitIds,
    };
  }, [scans, expectedSet]);

  const visibleScans = useMemo(
    () => (filterValue === 'all' ? scans : scans.filter((s) => s.status === filterValue)),
    [scans, filterValue],
  );

  const handleCommit = useCallback(async () => {
    setIsSubmitting(true);
    setSubmitError('');
    try {
      const result = await reconcileInventoryTravelers({
        productDocId,
        productVersionId: versionId,
        travelerIds: derived.commitIds,
      });
      const summary = `on hand ${result.previousOnHand} -> ${result.nextOnHand}.`
        + ` Added ${result.addedCount}, removed ${result.removedCount}, moved ${result.movedCount}`;
      toast.success(`Reconciled ${product?.sku ?? ''} / ${versionId}: ${summary}.`);
      navigate(`/inventory/${productDocId}/${versionId}`);
    } catch (err) {
      setSubmitError(err.message || 'Failed to reconcile inventory.');
    } finally {
      setIsSubmitting(false);
    }
  }, [productDocId, versionId, derived, product, navigate]);

  if (productLoading || versionLoading || expectedLoading || inventoryStatus === 'loading') {
    return <StatusIndicator type="loading">Loading reconciliation session…</StatusIndicator>;
  }
  if (productError) {
    return <Alert type="error">{productError.message}</Alert>;
  }
  if (expectedError) {
    const expectedErrorMessage = `Could not load expected inventory: ${expectedError.message}`;
    return <Alert type="error">{expectedErrorMessage}</Alert>;
  }
  if (!product || !version) {
    return <Alert type="error">Product or version not found.</Alert>;
  }

  const startedAtText = startedAtRef.current.format('YYYY-MM-DD HH:mm');
  const description = `${product.sku} · Version ${versionId} · `
    + `session started ${startedAtText} by ${operatorEmail}`;
  const newOnHand = derived.commitIds.length;
  const onHand = inventory?.onHand ?? 0;

  return (
    <SpaceBetween size="l">
      <Header
        variant="h1"
        description={description}
        actions={(
          <Button
            disabled={derived.commitIds.length === 0 && onHand === 0 && derived.expected === 0}
            disabledReason="Nothing to reconcile — no scans and no recorded inventory."
            onClick={() => { setSubmitError(''); setConfirmOpen(true); }}
          >
            End &amp; commit
          </Button>
        )}
      >
        Finished-goods reconciliation
      </Header>

      <Grid gridDefinition={GRID_DEFINITION}>
        <ScanFeed
          counted={derived.counted}
          sessionCount={scans.length}
          totalScans={scans.length}
          visibleScans={visibleScans}
          filterValue={filterValue}
          onFilterChange={setFilterValue}
          onScan={processScan}
          inlineError={inlineError}
        />
        <SpaceBetween size="l">
          <SessionProgress
            counted={derived.counted}
            expected={derived.expected}
            missing={derived.missing}
            net={derived.net}
            percent={derived.percent}
          />
          <Discrepancies
            missing={derived.missingIds.map((id) => ({ id }))}
            unexpected={derived.notExpected.map((s) => ({ id: s.id }))}
            otherSku={derived.otherSku.map((s) => ({ id: s.id, otherSkuLabel: s.otherSkuLabel }))}
          />
        </SpaceBetween>
      </Grid>

      <Modal
        visible={confirmOpen}
        onDismiss={() => setConfirmOpen(false)}
        header="End & commit reconciliation"
        footer={(
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" disabled={isSubmitting} onClick={() => setConfirmOpen(false)}>
                Cancel
              </Button>
              <Button variant="primary" loading={isSubmitting} onClick={handleCommit}>
                Commit reconciliation
              </Button>
            </SpaceBetween>
          </Box>
        )}
      >
        <SpaceBetween size="s">
          <Box>
            {'This will set on-hand for '}
            <strong>{`${product.sku} / ${versionId}`}</strong>
            {' from '}
            <strong>{onHand}</strong>
            {' to '}
            <strong>{newOnHand}</strong>
            {`  (${derived.unexpected} unexpected added, ${derived.missing} missing scrapped, `}
            {`${derived.otherSku.length} other-SKU ignored).`}
          </Box>
          {onHand !== derived.expected ? (
            <Alert type="info">
              {`The on-hand counter (${onHand}) doesn't match the ${derived.expected} traveler`}
              {' record(s) currently attached to this inventory — committing also corrects'}
              {' this drift.'}
            </Alert>
          ) : null}
          <Alert type="warning" header="Make sure every unit in the bin has been scanned.">
            Any traveler currently on-hand for this version but not scanned will be removed from
            inventory.
          </Alert>
          {submitError ? <Alert type="error">{submitError}</Alert> : null}
        </SpaceBetween>
      </Modal>
    </SpaceBetween>
  );
}
